import { AppStaticURL } from '@app/app.settings';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { _deviceType } from '@app/utils/device';
import { Router, ActivatedRoute } from '@angular/router';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  NEW_PASSWORD_VALIDATORS,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH
} from '@app/core/validators/password.constants';
import { BreadcrumbsDataModel } from '@app/core/models/breadcrumbs';
import { RoutingConfig } from '@app/routing/routing.config';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DialogService } from 'primeng/dynamicdialog';
import { DialogIncludesTypes, DialogComponentModel } from '@app/core/models/diallog';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { EmailStringValidate, PhoneNumberValidate, URLLinkValidate } from '@app/utils/validator';
import { AuthService } from '@app/modules/auth-modules/auth.service';
import { AccountService } from '@app/modules/account-modules/account.service';
import { AccountStoryService } from '@app/modules/account-modules/account.story';
import { AccountSignUpRequest, AuthResponseSuccess } from '@app/modules/auth-modules/auth.model';
import { MusicianService } from '@app/modules/musician-modules/musician.service';
import { MusicianSignUpRequest } from '@app/modules/musician-modules/musician.model';
import { SearchActivityComponent } from '@app/modules/activity-modules/search-activity/search-activity.component';
import { CityModel, GenresModel, InstrumentsModel, PositionModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { OAuthService, OAuthTypes } from '@app/modules/auth-modules/oauth.service';
import { AuthEventBus, AuthStoryService } from '@app/modules/auth-modules/auth.story';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FileToBase64, ObjectRequestFormatted } from '@app/utils/converter';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import * as moment from 'moment';
declare var $;

import { SearchCityComponent } from '@app/modules/cities-module/search-city/search-city.component';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import { CreateBookingCustomerDto, CreateVenueDto } from '@app/modules/booking-modules/booking.model';
import { SocialLinkDto, SocialPlatform } from '@app/modules/account-modules/performers.model';
import { SOCIAL_PLATFORMS } from '@app/modules/performers-modules/client/performer-form.constants';
import {
  VENUE_FACILITY_OPTION_KEYS,
  validateVenueSignUpForm,
  VenueInnValidationState
} from '@app/shared/venue-customer-profile-fields/venue-customer-form.constants';
import { FundamentalsService } from '@app/modules/fundamentals-modules/fundamentals.service';

export interface BaseErrorModel {
  status: boolean;
  value: string;
}
export interface SignUpErrorModels {
  email: BaseErrorModel;
  firstName: BaseErrorModel;
  cityId: BaseErrorModel;
  phone: BaseErrorModel;
  password: BaseErrorModel;
  password_repeat: BaseErrorModel;
}

@UntilDestroy()
@Component({
  selector: 'app-sign-up',
  templateUrl: 'sign-up.component.html',
  styleUrls: [`./sign-up.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class SignUpComponent extends DevicesClass implements OnInit {
  public Routing = RoutingConfig;
  public OAuthTypes = OAuthTypes;
  public BreadcrumbsData: BreadcrumbsDataModel = [
    {
      title: 'Регистрация',
      url: `/${RoutingConfig.Login}/${RoutingConfig.SignUp}`,
      active: true
    }
  ];

  public SignUpForm: FormGroup;
  public SignUpLoading: boolean;
  public SignUpAvatar: string;
  public SignUpAvatarFile: any = null;
  public SignUpExternal: boolean;
  public SignUpCitySubject: ReplaySubject<CityModel> = new ReplaySubject<CityModel>(1);
  public SignUpCityValue: CityModel = null;
  public SignUpCustomerType: 'individual' | 'venue' = 'individual';
  public venueInnValidationError: string = '';
  public venueInnValidationPending: boolean = false;
  public venueInnValidatedInn = '';

  readonly passwordMinLen = PASSWORD_MIN_LENGTH;
  readonly passwordMaxLen = PASSWORD_MAX_LENGTH;

  readonly SignUpActivitySubject = new ReplaySubject<any>(1);
  signUpActivityPosition: PositionModel | null = null;
  signUpActivityInstruments: InstrumentsModel = [];

  readonly SocialPlatforms = SOCIAL_PLATFORMS;
  public Genres: GenresModel = [];

  public ErrorsField: SignUpErrorModels;
  public ErrorsText: string;

  public fileAcceptString = 'image/x-png,image/gif,image/jpeg';

  constructor(injector: Injector,
    public cdr: ChangeDetectorRef,
    public oauthService: OAuthService,
    private router: Router,
    public route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private messageService: MessageService,
    private accountService: AccountService,
    private accountStory: AccountStoryService,
    private authService: AuthService,
    private authStory: AuthStoryService,
    private bookingService: BookingService,
    private musicianService: MusicianService,
    private fundamentalsService: FundamentalsService,
    private notificationService: NotificationService,
    private primengConfig: PrimeNGConfig,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.primengConfig.ripple = true;

    this.SignUpForm = this.formBuilder.group({
      avatarPath: [''],
      oauthId: [''],
      cityId: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: [''],
      email: ['', Validators.compose([
        Validators.required, Validators.pattern(EmailStringValidate())
      ])],
      phone: [''],
      password: ['', NEW_PASSWORD_VALIDATORS],
      password_repeat: ['', NEW_PASSWORD_VALIDATORS],
      processing: [false],
      birthday: [''],
      hasTelegram: [false],
      hasMax: [false],
      socialLinks: this.formBuilder.array([] as FormGroup[]),
      venueName: [''],
      inn: [''],
      format: [''],
      description: ['', Validators.maxLength(1000)],
      venueTypeTag: [''],
      preferredGenres: [[]],
      preferredPerformerTypes: [[]],
      readyToAcceptApplications: [false],
      addressFullAddress: [''],
      addressDescription: [''],
      maxAudienceCapacity: [null],
      stageAreaSquareMeters: [null],
      timeLimit: ['']
    });
    VENUE_FACILITY_OPTION_KEYS.forEach(key => {
      this.SignUpForm.addControl(`facility_${key}_level`, this.formBuilder.control(''));
      this.SignUpForm.addControl(`facility_${key}_notes`, this.formBuilder.control(''));
    });

    this.ErrorsField = {
      email: {status: false, value: ''},
      firstName: {status: false, value: ''},
      cityId: {status: false, value: ''},
      phone: {status: false, value: ''},
      password: {status: false, value: ''},
      password_repeat: {status: false, value: ''}
    };

    this.onSignUpCtrl();
    this.onSignUpOAuthCtrl();
    this.onCityCtrl();

    this.onAuthStoryBus();

    if (this.route.snapshot.queryParams['intent'] === 'bookingIndividual') {
      this.addBookingSocialLink();
      this.Genres = this.fundamentalsService.genresDependencies || [];
    }

    if (this.route.snapshot.queryParams['intent'] === 'musician') {
      this.SignUpActivitySubject.pipe(untilDestroyed(this)).subscribe(value => {
        this.signUpActivityPosition = value?.position || null;
        this.signUpActivityInstruments = value?.instruments || [];
        this.cdr.markForCheck();
      });
    }
  }

  onViewSearchActivityForMusician(): void {
    this.dialogService.open(SearchActivityComponent, {
      header: 'Выбрать инструмент и должность',
      width: _deviceType() === DeviceTypes.Mobile ? '90%' : '100%',
      styleClass: 'p-dialog__activity-search',
      data: <DialogComponentModel>{
        includeType: DialogIncludesTypes.ComponentTemplate,
        includeData: {
          dataInitial: {
            position: this.signUpActivityPosition,
            instruments: this.signUpActivityInstruments
          },
          dataSubject: this.SignUpActivitySubject
        }
      }
    });
  }

  get bookingSocialLinksArray(): FormArray {
    return this.SignUpForm.get('socialLinks') as FormArray;
  }

  private createBookingSocialLinkGroup(): FormGroup {
    return this.formBuilder.group({
      platform: [SocialPlatform.Instagram],
      url: [
        '',
        (c: AbstractControl): ValidationErrors | null => {
          const v = (c.value || '').toString().trim();
          return v && !URLLinkValidate(v) ? { urlInvalid: true } : null;
        }
      ]
    });
  }

  addBookingSocialLink(): void {
    this.bookingSocialLinksArray.push(this.createBookingSocialLinkGroup());
    this.cdr.markForCheck();
  }

  removeBookingSocialLink(i: number): void {
    this.bookingSocialLinksArray.removeAt(i);
    this.cdr.markForCheck();
  }

  trackByBookingSocialIndex(index: number): number {
    return index;
  }

  private getValidBookingSocialLinks(): SocialLinkDto[] {
    const rows = this.bookingSocialLinksArray?.controls as FormGroup[] | undefined;
    if (!rows?.length) {
      return [];
    }
    return rows
      .map(ctrl => ({
        platform: ctrl.get('platform')?.value as SocialPlatform,
        url: (ctrl.get('url')?.value || '').trim()
      }))
      .filter(l => !!l.url && URLLinkValidate(l.url));
  }

  setCustomerType(type: 'individual' | 'venue'): void {
    this.SignUpCustomerType = type;
    if (type !== 'venue') {
      this.venueInnValidationError = '';
      this.venueInnValidationPending = false;
      this.venueInnValidatedInn = '';
    }
    this.cdr.markForCheck();
  }

  private pickVenueRegistrationFailureMessage(
    failures: Record<string, string[]> | undefined
  ): string | null {
    if (!failures || this.SignUpCustomerType !== 'venue') {
      return null;
    }
    const order = [
      'inn',
      'address',
      'venueTypeTag',
      'preferredGenres',
      'preferredPerformerTypes',
      'facilities',
      'venueName'
    ];
    for (const key of order) {
      const msgs = failures[key];
      if (!Array.isArray(msgs) || !msgs.length) {
        continue;
      }
      const russian = msgs.find(m => /[а-яА-ЯёЁ]/.test(m));
      const text = (russian || msgs[0]).trim();
      if (text) {
        return text;
      }
    }
    return null;
  }

  onSignUpLoading(status: boolean, delay: number = 0) {
    setTimeout(() => {
      this.SignUpLoading = status;
      this.cdr.detectChanges();
    }, delay);
  }

  onAuthStoryBus(): void {
    this.authStory.AuthEventBus$.pipe(untilDestroyed(this)).subscribe(
      emit => this.onAuthStoryListener(emit)
    );
  }

  onAuthStoryListener(emit: AuthEventBus): void {
    if (emit.event === 'onOAuthSignIn') {
      this.authService.getToken(emit.data.token).subscribe(
        response => {
          this.authService.SignInExternal(response);

          this.accountService.onIniGetMyInfo();

          this.router.navigateByUrl(
            this.route.snapshot.queryParams['redirectUrl'] || RoutingConfig.Root
          ).catch();
        },
        error => {
          this.messageService.add({
            key: 'signUpToast',
            severity:'error',
            summary: 'Ошибка',
            detail: 'Не удалось выполнить вход через социальные сети.',
            life: 30000000
          });
        }
      );
    }

    if (emit.event === 'onOAuthSignUp') {
      this.onSignUpOAuthApply(emit.data);
    }

    if (emit.event === 'onOAuthSignError') {
      this.messageService.add({
        key: 'signUpToast',
        severity:'error',
        summary: 'Ошибка',
        detail: 'Не удалось выполнить вход через социальные сети.',
        life: 30000000
      });
    }
  }

  onSignUpCtrl(): void {
    for (const key of Object.keys(this.ErrorsField)) {
      if (this.SignUpForm.controls.hasOwnProperty(key)) {
        this.SignUpForm.controls[key].valueChanges.pipe(untilDestroyed(this)).subscribe(
          value => {
            this.ErrorsField[key].status = false;
            this.ErrorsText = '';
            this.onToggleViewErrors(false);
          }
        )
      }
    }
    this.SignUpForm.controls.processing.valueChanges.pipe(untilDestroyed(this)).subscribe(
      value => {
        this.ErrorsText = '';
        this.onToggleViewErrors(false);
      }
    );
    this.SignUpForm.controls.inn.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.venueInnValidationError = '';
      this.venueInnValidationPending = false;
      this.venueInnValidatedInn = '';
      this.cdr.markForCheck();
    });
  }

  onSignUpOAuthCtrl(): void {
    this.route.queryParamMap.subscribe((params: any) => {
      this.onSignUpOAuthApply(params.params || {});
    });
  }

  onSignUpOAuthApply(response: any): void {
    for (const key of Object.keys(response)) {
      if (key === 'OauthId') {
        this.SignUpExternal = true;
        this.SignUpForm.controls.oauthId.setValue(response[key]);
      }
      if (key === 'AvatarPath') {
        this.SignUpForm.controls.avatarPath.setValue(response[key]);
        this.SignUpAvatar = (AppStaticURL + response[key]);
      }
      if (key === 'FirstName') {
        this.SignUpForm.controls.firstName.setValue(response[key]);
      }
      if (key === 'LastName') {
        this.SignUpForm.controls.lastName.setValue(response[key]);
      }
      if (key === 'Email') {
        this.SignUpForm.controls.email.setValue(response[key]);
      }
    }
    this.cdr.markForCheck();
  }

  onCityCtrl(): void {
    this.SignUpCitySubject.pipe(untilDestroyed(this)).subscribe(
      value => {
        this.SignUpCityValue = value;
        this.SignUpForm.controls.cityId.setValue(value.id);

        this.cdr.markForCheck();
      }
    );
  }

  onUploadAvatarHandler(): void {
    const UploadInput = document.getElementById('avatarUpload');
    UploadInput.click();
  }

  onUploadAvatarEvent(files: FileList): void {
    for (const i of Object.keys(files)) {
      const File: File = files[i];

      if ((File.size / 1024) > 1024) {
        return this.messageService.add({
          key: 'signUpToast',
          severity:'error',
          summary: 'Ошибка загрузки',
          detail: 'Размер файла слишком большой',
          life: 30000000
        });
      }

      FileToBase64(File).then((value: string) => {
        this.SignUpAvatarFile = files;
        this.SignUpAvatar = value;
        this.cdr.markForCheck();
      })
    }
  }

  onUploadAvatarClear(): void {
    this.SignUpAvatar = null;
    this.cdr.markForCheck();
  }

  onToggleViewErrors(show: boolean): void {
    const SignUpErrors = $('.__sign-up__errors');
    show ? SignUpErrors.slideDown(150) : SignUpErrors.slideUp(150);
  }

  onCheckInput(key: string): void {
    if (this.SignUpForm.controls.hasOwnProperty(key) && this.SignUpForm.controls[key].value !== '') {
      this.ErrorsField[key].status = !this.SignUpForm.controls[key].valid;
    }
  }

  onCheckPhone(): void {
    this.ErrorsField.phone.status = !this.onContactPhoneValid;
  }

  onMaskPhone($event: any, backspace?: boolean): void {
    const phone: string = this.SignUpForm.controls.phone.value || null;

    if (!phone || phone === '') {
      return;
    }

    if (phone.split('').length === 1) {
      if ($event.inputType === 'deleteContentBackward') {
        this.SignUpForm.controls.phone.setValue(phone.split('').slice(1).join(''));
      } else {
        this.SignUpForm.controls.phone.setValue('7' + phone);
      }
    }
  }

  onCoincidencePassword(): boolean {
    if (this.SignUpExternal) {
      return true;
    }
    return this.SignUpForm.controls.password.value === this.SignUpForm.controls.password_repeat.value;
  }

  onGetFormattedDate(date: string): string {
    return date ? moment(date, 'DD.MM.YYYY').toISOString() : ''
  }

  onSetMessengerValue(key: 'hasTelegram' | 'hasMax'): void {
    const currentValue = !!this.SignUpForm.controls[key].value;
    this.SignUpForm.controls[key].setValue(!currentValue);
  }

  onGetMessengerStatus(key: 'hasTelegram' | 'hasMax'): boolean {
    return !!this.SignUpForm.controls[key].value;
  }

  onVenueInnValidationStateChange(state: VenueInnValidationState): void {
    this.venueInnValidationError = state.error;
    this.venueInnValidationPending = state.pending;
    this.venueInnValidatedInn = state.validatedInn ?? '';
    this.cdr.markForCheck();
  }

  private buildVenueFacilitiesPayload(v: Record<string, unknown>): CreateVenueDto['facilities'] {
    const facilities: Record<string, unknown> = {};
    VENUE_FACILITY_OPTION_KEYS.forEach(key => {
      const level = (v[`facility_${key}_level`] || '').toString().trim();
      const notes = (v[`facility_${key}_notes`] || '').toString().trim();
      if (level || notes) {
        facilities[key] = { level: level || null, notes: notes || null };
      }
    });
    if (v.maxAudienceCapacity != null && v.maxAudienceCapacity !== '') {
      facilities.maxAudienceCapacity = Number(v.maxAudienceCapacity);
    }
    if (v.stageAreaSquareMeters != null && v.stageAreaSquareMeters !== '') {
      facilities.stageAreaSquareMeters = Number(v.stageAreaSquareMeters);
    }
    if (v.timeLimit) {
      facilities.timeLimit = (v.timeLimit || '').toString().trim() || null;
    }
    return Object.keys(facilities).length ? (facilities as CreateVenueDto['facilities']) : null;
  }

  onSubmitForm(): void {
    if (this.SignUpLoading) {
      return;
    }

    for (const field of Object.keys(this.ErrorsField)) {
      if (this.ErrorsField[field].status) {
        this.ErrorsText = 'Проверьте правильность заполненных полей!';
        return this.onToggleViewErrors(true);
      }
    }

    if (!this.SignUpForm.controls.processing.value) {
      this.ErrorsText = 'Необходимо согласиться с обработкой персональных данных!';
      this.onToggleViewErrors(true);
      return;
    }

    if (this.SignUpExternal) {
      this.SignUpForm.removeControl('password');
      this.SignUpForm.removeControl('password_repeat');
    }

    if (!this.SignUpForm.valid) {
      for (const key of Object.keys(this.SignUpForm.controls)) {
        if (this.ErrorsField.hasOwnProperty(key)) {
          const ctrl = this.SignUpForm.controls[key];
          this.ErrorsField[key].status = !ctrl.valid;

          if (key === 'password' || key === 'password_repeat') {
            this.ErrorsField[key].value = ctrl.valid ? '' : this.passwordFieldErrorMessage(ctrl);
          }

          if (key === 'cityId' && this.SignUpForm.controls[key].value === 0) {
            this.ErrorsField[key].status = true;
          }
        }
      }

      this.ErrorsText = 'Укажите имя, e-mail, город и пароль с подтверждением.';
      this.onToggleViewErrors(true);
      return;
    }

    if (!this.onCoincidencePassword()) {
      this.ErrorsText = 'Пароли не совпадают!';
      this.ErrorsField.password.status = true;
      this.ErrorsField.password_repeat.status = true;
      this.ErrorsField.password_repeat.value = 'Должен совпадать с паролем';
      this.onToggleViewErrors(true);
      return;
    }

    const intentPre = this.route.snapshot.queryParams['intent'] || null;
    if (intentPre === 'musician') {
      const raw = this.SignUpForm.getRawValue();
      const birthdayRaw = (raw.birthday || '') as string;
      const birthdayParsed = moment(birthdayRaw, 'DD.MM.YYYY', true);
      const age = birthdayParsed.isValid() ? moment().diff(birthdayParsed, 'years') : -1;
      if (!raw.lastName || !String(raw.lastName).trim()) {
        this.ErrorsText = 'Укажите фамилию.';
        this.onToggleViewErrors(true);
        return;
      }
      if (!birthdayParsed.isValid() || age < 18) {
        this.ErrorsText = 'Укажите корректную дату рождения и возраст 18+.';
        this.onToggleViewErrors(true);
        return;
      }
      const pos = this.signUpActivityPosition;
      const inst = this.signUpActivityInstruments || [];
      if (!inst.length) {
        this.ErrorsText = 'Выберите хотя бы один инструмент.';
        this.onToggleViewErrors(true);
        return;
      }
      if (!pos || pos.id === undefined || pos.id === null) {
        this.ErrorsText = 'Укажите должность или вариант «без должности» в диалоге.';
        this.onToggleViewErrors(true);
        return;
      }
    }

    const SignUpValue = this.SignUpForm.getRawValue();
    const intent = this.route.snapshot.queryParams['intent'] || null;

    if (intent === 'bookingIndividual') {
      const birthday = SignUpValue.birthday || '';
      const birthdayParsed = moment(birthday, 'DD.MM.YYYY', true);
      const age = birthdayParsed.isValid() ? moment().diff(birthdayParsed, 'years') : -1;
      const phone = SignUpValue.phone || '';
      if (!SignUpValue.lastName || !String(SignUpValue.lastName).trim()) {
        this.ErrorsText = 'Для регистрации заказчика укажите фамилию.';
        this.onToggleViewErrors(true);
        return;
      }
      if (!birthdayParsed.isValid() || age < 18) {
        this.ErrorsText = 'Для регистрации заказчика требуется корректная дата рождения и возраст 18+.';
        this.onToggleViewErrors(true);
        return;
      }
      if (this.getValidBookingSocialLinks().length < 1) {
        this.ErrorsText = 'Добавьте хотя бы одну корректную ссылку на соцсеть (площадка и URL).';
        this.onToggleViewErrors(true);
        return;
      }
      if (!phone || !PhoneNumberValidate(phone)) {
        this.ErrorsText = 'Для регистрации заказчика укажите корректный телефон.';
        this.ErrorsField.phone.status = true;
        this.onToggleViewErrors(true);
        return;
      }
      if (this.SignUpCustomerType === 'venue') {
        const venueError = validateVenueSignUpForm(SignUpValue as Record<string, unknown>, {
          error: this.venueInnValidationError,
          pending: this.venueInnValidationPending,
          validatedInn: this.venueInnValidatedInn
        });
        if (venueError) {
          this.ErrorsText = venueError;
          this.onToggleViewErrors(true);
          return;
        }
      }
    }

    this.onSignUpLoading(true);

    const accountSignUp = ObjectRequestFormatted(<AccountSignUpRequest>{
      firstName: SignUpValue.firstName,
      lastName: SignUpValue.lastName,
      email: SignUpValue.email,
      phone: (SignUpValue.phone || '').split('').slice(1).join(''),
      birthday: this.onGetFormattedDate(SignUpValue.birthday),
      cityId: SignUpValue.cityId,
      password: SignUpValue.password,
      oauthId: SignUpValue.oauthId || null,
    });

    if (intent === 'musician') {
      const musicianPayload = ObjectRequestFormatted(<MusicianSignUpRequest>{
        firstName: SignUpValue.firstName,
        lastName: SignUpValue.lastName,
        email: SignUpValue.email,
        phone: (SignUpValue.phone || '').split('').slice(1).join(''),
        birthday: this.onGetFormattedDate(SignUpValue.birthday),
        cityId: SignUpValue.cityId,
        password: SignUpValue.password,
        oauthId: SignUpValue.oauthId || null,
        positionId: this.signUpActivityPosition!.id,
        instrumentIds: this.signUpActivityInstruments.map(i => i.id)
      });
      this.musicianService.musicianSignUp(musicianPayload).subscribe(
        response => {
          this.onSignUpLoading(false);
          this.authService.onAuthRequestSuccess(response, true);
          this.onSignUpAccountUserAPI();
          this.router
            .navigateByUrl(
              this.route.snapshot.queryParams['redirectUrl'] || '/' + RoutingConfig.PerformersList
            )
            .catch();
        },
        error => this.onErrorsCallback(error)
      );
      return;
    }

    if (intent === 'bookingIndividual') {
      const representer = {
        email: SignUpValue.email,
        name: SignUpValue.firstName,
        lastName: SignUpValue.lastName || '',
        birthDate: this.onGetFormattedDate(SignUpValue.birthday),
        cityId: Number(SignUpValue.cityId) || undefined,
        phoneNumber: {
          number: (SignUpValue.phone || '').split('').slice(1).join(''),
          hasTelegram: !!SignUpValue.hasTelegram,
          hasMax: !!SignUpValue.hasMax
        }
      };
      const socialLinks = this.getValidBookingSocialLinks();

      if (this.SignUpCustomerType === 'venue') {
        const innDigits = String(SignUpValue.inn || '').replace(/\D/g, '');
        const venuePayload: CreateVenueDto = {
          representer,
          contacts: representer.phoneNumber,
          socialLinks,
          password: SignUpValue.password,
          venueName: String(SignUpValue.venueName).trim(),
          inn: innDigits,
          format: SignUpValue.format ? String(SignUpValue.format).trim() : null,
          description: SignUpValue.description ? String(SignUpValue.description).trim() : null,
          venueTypeTag: String(SignUpValue.venueTypeTag).trim(),
          preferredGenres: SignUpValue.preferredGenres,
          preferredPerformerTypes: SignUpValue.preferredPerformerTypes,
          readyToAcceptApplications: !!SignUpValue.readyToAcceptApplications,
          address: {
            fullAddress: String(SignUpValue.addressFullAddress).trim(),
            description: SignUpValue.addressDescription
              ? String(SignUpValue.addressDescription).trim()
              : null
          },
          facilities: this.buildVenueFacilitiesPayload(SignUpValue as Record<string, unknown>)!
        };
        this.registerBookingWithOptionalPicKey(picKey =>
          this.bookingService.registerVenueCustomer({ ...venuePayload, picKey: picKey || undefined })
        ).subscribe(
          (tokenResponse: AuthResponseSuccess) => {
            this.onSignUpLoading(false);
            this.authService.onAuthRequestSuccess(tokenResponse, true);
            this.onSignUpAccountUserAPI();
            this.router.navigateByUrl(
              this.route.snapshot.queryParams['redirectUrl'] || RoutingConfig.Root
            ).catch();
          },
          error => this.onErrorsCallback(error)
        );
        return;
      }

      const payload: CreateBookingCustomerDto = {
        representer,
        socialLinks,
        password: SignUpValue.password
      };
      this.registerBookingWithOptionalPicKey(picKey =>
        this.bookingService.registerIndividualCustomer({ ...payload, picKey: picKey || undefined })
      ).subscribe(
        (tokenResponse: AuthResponseSuccess) => {
          this.onSignUpLoading(false);
          this.authService.onAuthRequestSuccess(tokenResponse, true);
          this.onSignUpAccountUserAPI();
          this.router.navigateByUrl(
            this.route.snapshot.queryParams['redirectUrl'] || RoutingConfig.Root
          ).catch();
        },
        error => this.onErrorsCallback(error)
      );
      return;
    }

    this.authService.signUp(accountSignUp).subscribe(
      response => {
        this.onSignUpLoading(false);

        this.authService.onAuthRequestSuccess(response, true);

        this.onSignUpAccountUserAPI();

        this.router.navigateByUrl(
          this.route.snapshot.queryParams['redirectUrl'] || RoutingConfig.Login + '/' + RoutingConfig.SignSuccess
        ).catch();
      },
      error => this.onErrorsCallback(error)
    );
  }

  private registerBookingWithOptionalPicKey(
    registerFn: (picKey: string | null) => Observable<AuthResponseSuccess>
  ): Observable<AuthResponseSuccess> {
    return this.uploadBookingPicKeyIfNeeded().pipe(switchMap(picKey => registerFn(picKey)));
  }

  private uploadBookingPicKeyIfNeeded(): Observable<string | null> {
    const file = this.SignUpAvatarFile?.[0];
    if (!file || this.route.snapshot.queryParams['intent'] !== 'bookingIndividual') {
      return of(null);
    }
    return this.bookingService.uploadBookingCustomerPhoto(file).pipe(map(key => (key ? String(key) : null)));
  }

  onSignUpAccountUserAPI(): void {
    if (this.SignUpAvatarFile && this.route.snapshot.queryParams['intent'] !== 'bookingIndividual') {
      this.accountService.uploadAvatar(this.SignUpAvatarFile).pipe(
        untilDestroyed(this)
      ).subscribe(
        () => this.onUpdateUserInfo(),
        err => this.onErrorsCallback(err)
      );
      return;
    }
    this.onUpdateUserInfo();
  }

  onUpdateUserInfo(): void {
    this.accountService.getMyInfo().subscribe(
      accountResponse => {
        this.accountService.onSetUserRole(accountResponse);

        this.accountStory.AccountEventBus.next({
          event: 'onAccountUpdate',
          data: accountResponse
        });

        this.onSignUpLoading(false);
      },
      accountError => this.onErrorsCallback(accountError)
    );
  }

  onErrorsCallback(error): void {
    this.onSignUpLoading(false);

    if (error.statusCode === 400) {
      return this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: error.message
      });
    }

    if (error.hasOwnProperty('failures')) {
      const failures = error.failures as Record<string, string[]>;
      for (const key of Object.keys(failures)) {
        const Failures = failures[key];

        if (this.ErrorsField.hasOwnProperty(key)) {
          this.ErrorsField[key].status = true;
          this.ErrorsField[key].value = Failures;
        }
      }
      if (this.SignUpCustomerType === 'venue' && failures.inn?.length) {
        this.venueInnValidationError = String(failures.inn[0]).trim();
        this.venueInnValidatedInn = '';
      }
    }

    if (error.hasOwnProperty('message')) {
      const venueFailureText = this.pickVenueRegistrationFailureMessage(error.failures);
      this.ErrorsText = venueFailureText || `${error.message}!`;
      this.onToggleViewErrors(true);
    }
  }

  onViewSearchCities(): void {
    this.dialogService.open(SearchCityComponent, {
      header: 'Выбрать город',
      width: _deviceType() === DeviceTypes.Mobile ? '90%' : '100%',
      styleClass: 'p-dialog__city-search',
      data: <DialogComponentModel>{
        includeType: DialogIncludesTypes.ComponentTemplate,
        includeData: {
          dataInitial: this.SignUpCityValue,
          dataSubject: this.SignUpCitySubject
        }
      }
    })
  }

  get onContactPhoneValid(): boolean {
    const phone: string = this.SignUpForm.controls.phone.value || null;

    return PhoneNumberValidate(phone);
  }

  private passwordFieldErrorMessage(ctrl: AbstractControl): string {
    const e = ctrl.errors;
    if (!e) {
      return '';
    }
    if (e.required) {
      return 'Обязательное поле.';
    }
    if (e.minlength) {
      return `Минимум ${PASSWORD_MIN_LENGTH} символов.`;
    }
    if (e.maxlength) {
      return `Не более ${PASSWORD_MAX_LENGTH} символов.`;
    }
    return '';
  }

}
